import {
  Box,
  Button,
  ScrollAreaRoot,
  ScrollAreaScrollbar,
  ScrollAreaViewport,
  Text,
} from '@streamelements/frontend-ui';
import { createFileRoute, Link, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { ToolbarButton } from '../../../components/ToolbarButton';
import { KeyboardArrowLeftRounded } from '@streamelements/frontend-icons';
import { StepsIndicatorItem, StepsIndicatorRoot } from '../-StepIndicator';
import { useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { FormValues } from './-form/form';
import { useMutation } from '@tanstack/react-query';
import { promisefy } from '../../../utils/promisefy';
import { OverlayThemesApiClient } from '../../../api/themes';
import { getEnabledSources } from '../../_authenticated.canvases/$canvasId/-sceneDropdownMenu/getEnabledSources';
import { SceneItemInfo } from '../../../types/obs';
import { InputSourcePropertiesClientProvider } from './-InputSourcePropertiesAdapter';

export const Route = createFileRoute('/_authenticated/onboarding/new-setup/_layout')({
  component: OnboardingNewSetupLayout,
  loader: async ({ context: { authContextProvider } }) => {
    await authContextProvider.getAuthContext();
  },
});

function useRoutes() {
  const router = useRouter();
  return useMemo(() => {
    return {
      index: router.buildLocation({ to: '/onboarding/new-setup' }),
      onboardingIndex: router.buildLocation({ to: '/onboarding' }),
      setupDisplayCapture: router.buildLocation({
        to: '/onboarding/new-setup/setup-display-capture',
      }),
      setupAlerts: router.buildLocation({
        to: '/onboarding/new-setup/setup-alerts',
      }),
      setupComplete: router.buildLocation({
        to: '/onboarding/new-setup/setup-complete',
      }),
    };
  }, [router]);
}

function useRoutesChain() {
  const routes = useRoutes();
  return useMemo(() => {
    return {
      index: routes.setupDisplayCapture,
      setupDisplayCapture: routes.setupAlerts,
      setupAlerts: routes.setupComplete,
      setupComplete: routes.setupComplete,
    };
  }, [routes]);
}

function useInverseRoutesChain() {
  const routes = useRoutes();
  return useMemo(() => {
    return {
      index: routes.onboardingIndex,
      setupDisplayCapture: routes.index,
      setupAlerts: routes.setupDisplayCapture,
      setupComplete: routes.index,
    };
  }, [routes]);
}

function useRoutesNames() {
  const routes = useRoutes();
  return useMemo(() => {
    return {
      [routes.index.href]: 'index' as const,
      [routes.setupDisplayCapture.href]: 'setupDisplayCapture' as const,
      [routes.setupAlerts.href]: 'setupAlerts' as const,
      [routes.setupComplete.href]: 'setupComplete' as const,
    };
  }, [routes]);
}

const routesCTA: Record<string, (formValues: FormValues) => string> = {
  index: () => 'Next',
  setupDisplayCapture: () => 'Next',
  setupAlerts: ({ overlayTheme }) => {
    if (!overlayTheme) {
      return 'Proceed without a theme';
    }

    return 'Next';
  },
  setupComplete: () => 'Finish setup',
};

async function handleAddMicrophone({
  sceneId,
  values,
  existingSources,
}: {
  sceneId: string;
  values: FormValues;
  existingSources: Record<SceneItemInfo['class'], SceneItemInfo[]>;
}) {
  const { audioInputDeviceId } = values;

  if (!audioInputDeviceId || audioInputDeviceId === 'none') {
    return;
  }

  const sourcePropertiesClient = await InputSourcePropertiesClientProvider.createSourcePropertiesClient();
  const { audioInputSourceTypeClass, audioInputIdPropertyName } = sourcePropertiesClient;

  const existingSource = existingSources[audioInputSourceTypeClass]?.find(
    (source) =>
      source.class === audioInputSourceTypeClass && source.settings?.[audioInputIdPropertyName] === audioInputDeviceId
  );

  return promisefy(window.host.addSceneItemObsNativeSource)({
    sceneId,
    videoCompositionId: 'default',
    class: audioInputSourceTypeClass,
    name: 'Microphone',
    settings: {
      [sourcePropertiesClient.audioInputIdPropertyName]: audioInputDeviceId,
      use_device_timing: false,
    },
    existingSourceId: existingSource?.id,
  } as never);
}

async function handleAddDisplayCapture({
  sceneId,
  values,
  existingSources,
}: {
  sceneId: string;
  values: FormValues;
  existingSources: Record<SceneItemInfo['class'], SceneItemInfo[]>;
}) {
  const { displayCaptureDeviceId } = values;

  if (!displayCaptureDeviceId) {
    return null;
  }

  const sourcePropertiesClient = await InputSourcePropertiesClientProvider.createSourcePropertiesClient();
  const { displayCaptureSourceTypeClass, displayCaptureIdPropertyName } = sourcePropertiesClient;

  const existingSource = existingSources[displayCaptureSourceTypeClass]?.find(
    (source) =>
      source.class === displayCaptureSourceTypeClass &&
      source.settings?.[displayCaptureIdPropertyName] === displayCaptureDeviceId
  );

  return promisefy(window.host.addSceneItemObsNativeSource)({
    sceneId,
    videoCompositionId: 'default',
    class: displayCaptureSourceTypeClass,
    name: 'Screen capture',
    settings: {
      monitor_id: displayCaptureDeviceId,
      method: 0,
    },
    existingSourceId: existingSource?.id,
  } as never);
}

async function handleAddVideoCapture({
  sceneId,
  values,
  cameraCoordinates,
  scaling,
  existingSources,
}: {
  sceneId: string;
  values: FormValues;
  cameraCoordinates: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  scaling: number;
  existingSources: Record<SceneItemInfo['class'], SceneItemInfo[]>;
}) {
  const { videoInputDeviceId } = values;

  if (!videoInputDeviceId || videoInputDeviceId === 'none') {
    return null;
  }

  const sourcePropertiesClient = await InputSourcePropertiesClientProvider.createSourcePropertiesClient();
  const { videoInputSourceTypeClass, videoInputIdPropertyName } = sourcePropertiesClient;

  const existingSource = existingSources[videoInputSourceTypeClass]?.find(
    (source) =>
      source.class === videoInputSourceTypeClass && source.settings?.[videoInputIdPropertyName] === videoInputDeviceId
  );

  const cameraSource = await promisefy(window.host.addSceneItemObsNativeSource)({
    sceneId,
    videoCompositionId: 'default',
    class: videoInputSourceTypeClass,
    name: 'Camera',
    settings: {
      [videoInputIdPropertyName]: videoInputDeviceId,
      deactivate_when_not_showing: true,
      use_preset: false,
    },
    existingSourceId: existingSource?.id,
  } as never);

  let boundingBox = {
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    id: '',
    videoCompositionId: '',
  };

  let shouldCancelGetCameraBoundingBox = false;

  const breakGetCameraBoundingBoxTimeout = setTimeout(() => {
    shouldCancelGetCameraBoundingBox = true;
  }, 5000);

  do {
    boundingBox = await promisefy(window.host.getSceneItemBoundingBoxInViewport)(cameraSource);

    // It can take a few retries for the camera source to be ready xD
    if (shouldCancelGetCameraBoundingBox) {
      break;
    }
  } while (!boundingBox || boundingBox.right - boundingBox.left <= 20); // Wait for the camera source to be ready

  clearTimeout(breakGetCameraBoundingBoxTimeout);

  const currentWidth = boundingBox.right - boundingBox.left;
  const currentHeight = boundingBox.bottom - boundingBox.top;

  const scaleX = (cameraCoordinates.width * scaling) / currentWidth;
  const scaleY = (cameraCoordinates.height * scaling) / currentHeight;

  const newComposition = {
    ...cameraSource.composition!,
    scale: { x: scaleX, y: scaleY },
    position: {
      x: cameraCoordinates.left * scaling,
      y: cameraCoordinates.top * scaling,
    },
  };

  const didCancelGetCameraBoundingBox = shouldCancelGetCameraBoundingBox;

  if (!didCancelGetCameraBoundingBox) {
    await promisefy(window.host.setSceneItemPropertiesById)({
      id: cameraSource.id,
      videoCompositionId: cameraSource.videoCompositionId,
      sceneId: cameraSource.sceneId,
      composition: newComposition,
    });
  }

  return cameraSource;
}

function greatestCommonFactor(a: number, b: number) {
  while (b !== 0) {
    const temp = b;
    b = a % b;
    a = temp;
  }
  return a;
}

function OnboardingNewSetupLayout() {
  const router = useRouter();
  const { matches } = useRouterState();
  const { authContextProvider, serviceEnvironment } = Route.useRouteContext();
  const routesChain = useRoutesChain();
  const inverseRoutesChain = useInverseRoutesChain();
  const routesNames = useRoutesNames();

  const lastMatch = matches[matches.length - 1];
  const { fullPath } = lastMatch;
  // Router matches have trailing slash, but buildLocation doesn't
  // Just in case they change it in the future, we'll check for it
  const sliceEnd = fullPath.endsWith('/') ? -1 : undefined;
  const currentStep = routesNames[fullPath.slice(0, sliceEnd)];
  const nextStep = routesChain[currentStep];
  const previousStep = inverseRoutesChain[currentStep];

  const form = useForm<FormValues>({
    defaultValues: {
      audioInputDeviceId: '',
      videoInputDeviceId: '',
      overlayTheme: null,
    },
  });

  const handleSubmitMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const { displayCaptureDeviceId, overlayTheme } = values;

      const addedSourcesResult: Record<SceneItemInfo['class'], SceneItemInfo> = {} as never;

      const existingSources = (await promisefy(window.host.getAllExistingVideoInputSources)()).reduce(
        (acc, source) => {
          acc[source.class] = acc[source.class] || [];
          acc[source.class].push(source);

          return acc;
        },
        {} as Record<SceneItemInfo['class'], SceneItemInfo[]>
      );

      if (!overlayTheme) {
        const scene = await promisefy(window.host.getCurrentScene)({
          videoCompositionId: 'default',
        });
        const microphone = await handleAddMicrophone({
          sceneId: scene.id,
          values,
          existingSources,
        });
        const displayCapture = await handleAddDisplayCapture({
          sceneId: scene.id,
          values,
          existingSources,
        });

        const { srcWidth = 1920, srcHeight = 1080 } = displayCapture?.composition || {};

        const gcf = greatestCommonFactor(srcWidth, srcHeight);
        const aspectRatio = srcWidth / gcf / (srcHeight / gcf);
        const scaledWidth = (srcWidth / aspectRatio) * 0.25; // 25% of the screen width
        const scaledHeight = (srcHeight / aspectRatio) * 0.25; // 25% of the screen height

        const camera = await handleAddVideoCapture({
          sceneId: scene.id,
          values,
          cameraCoordinates: {
            height: Math.round(scaledHeight),
            width: Math.round(scaledWidth),
            left: 0,
            top: 0,
          },
          scaling: 1,
          existingSources,
        });

        console.info('Added sources', { microphone, displayCapture, camera });

        return;
      }

      const overlayThemesApiClient = new OverlayThemesApiClient({
        authContextProvider,
        serviceEnvironment,
      });

      const { themeId } = overlayTheme;
      const coordinates = OverlayThemesApiClient.themesSourcesCoordinates[themeId];

      const overlays = await overlayThemesApiClient.createOverlaysFromTheme({
        themeId,
      });
      const availableInputSourceTypes = await getEnabledSources();

      const {
        channel: { apiToken },
      } = await authContextProvider.getAuthContext();

      const { default: defaultComposition } = await promisefy(window.host.getAllVideoCompositions)();
      const sourcePropertiesClient = await InputSourcePropertiesClientProvider.createSourcePropertiesClient();

      const { displayCaptureSourceTypeClass, audioInputSourceTypeClass, videoInputSourceTypeClass } =
        sourcePropertiesClient;

      for (const overlay of overlays) {
        const scaling = Number(
          Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
            defaultComposition.videoFrame.width / overlay.settings.width
          )
        );
        const { name } = overlay;
        const nameLowercase = name.toLowerCase();

        const scene = await promisefy(window.host.addScene)({
          name,
        });

        /**
         * Theme/Overlay titles containing "talk" "talk scene", "in game" or "ingame"
         */
        const isTalkScene = nameLowercase.match(/(talk( scene)?)/);
        const isInGameScene = nameLowercase.match(/(in(\s)?game)/);

        const sceneName = isTalkScene ? 'yapping' : isInGameScene ? 'ingame' : null;
        const cameraCoordinates = sceneName ? coordinates.camera?.[sceneName] : null;
        const screenCaptureCoordinates = sceneName ? coordinates.screenCapture?.[sceneName] : null;

        const didPickScreenCapture = displayCaptureDeviceId && displayCaptureDeviceId !== 'none';
        const shouldAddScreenCapture = didPickScreenCapture && isInGameScene && screenCaptureCoordinates;

        const existingSources = (await promisefy(window.host.getAllExistingVideoInputSources)()).reduce(
          (acc, source) => {
            acc[source.class] = acc[source.class] || [];
            acc[source.class].push(source);

            return acc;
          },
          {} as Record<SceneItemInfo['class'], SceneItemInfo[]>
        );

        const existingAudioSources = (await promisefy(window.host.getAllExistingAudioInputSources)()).reduce(
          (acc, source) => {
            acc[source.class] = acc[source.class] || [];
            acc[source.class].push(source);

            return acc;
          },
          {} as Record<SceneItemInfo['class'], SceneItemInfo[]>
        );

        if (shouldAddScreenCapture) {
          const settings = {
            monitor_id: displayCaptureDeviceId,
            method: 0,
            width: screenCaptureCoordinates.width,
            height: screenCaptureCoordinates.height,
          };

          const existingSource = existingSources[displayCaptureSourceTypeClass]?.find(
            (source) => source.settings?.monitor_id === displayCaptureDeviceId
          );

          await availableInputSourceTypes[displayCaptureSourceTypeClass]?.callback({
            name: 'Screen capture',
            sceneId: scene.id,
            compositionId: 'default',
            settings,
            composition: {
              position: {
                y: screenCaptureCoordinates.top * scaling,
                x: screenCaptureCoordinates.left * scaling,
              },
              scale: { x: scaling, y: scaling },
            } as never,
            existingSourceId: existingSource?.id,
          });
        }

        if ((isTalkScene || isInGameScene) && cameraCoordinates) {
          addedSourcesResult[videoInputSourceTypeClass] = (await handleAddVideoCapture({
            sceneId: scene.id,
            values,
            cameraCoordinates,
            scaling,
            existingSources,
          }))!;

          addedSourcesResult[videoInputSourceTypeClass]
            ? console.info('Created camera source')
            : console.warn(
                'Failed to create camera source because the camera took too long to load (more than 5 seconds)'
              );
        }

        if (isTalkScene || isInGameScene) {
          addedSourcesResult[audioInputSourceTypeClass] = (await handleAddMicrophone({
            sceneId: scene.id,
            values,
            existingSources: existingAudioSources,
          }))!;
        }

        const overlaySource = await availableInputSourceTypes.browser_source?.callback({
          name: `${name} - StreamElements Overlay`,
          sceneId: scene.id,
          compositionId: 'default',
          settings: {
            url: `${serviceEnvironment.homepageProductBaseUrl}/overlay/${overlay._id}/${apiToken}`,
            width: overlay.settings.width,
            height: overlay.settings.height,
          },
          composition: {
            position: { x: 0, y: 0 },
            scale: {
              x: scaling,
              y: scaling,
            },
          } as never,
        });

        console.info(`Added scene for ${overlay.name} along with a browser source`, overlaySource);
      }
    },
  });

  return (
    <Box
      as="form"
      css={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
      }}
      onSubmit={(e) => {
        switch (currentStep) {
          case 'setupAlerts':
            if (handleSubmitMutation.isPending) {
              return 'Setting you up...';
            }

            form.handleSubmit((values) => {
              handleSubmitMutation.mutate(values, {
                onSuccess: () => {
                  router.navigate({ to: nextStep.href });
                },
                onError: console.log,
              });
            })(e);
            break;
          default:
            break;
        }
      }}
    >
      <Box
        as="header"
        css={{
          px: '0.5rem',
          py: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          mb: '0.5rem',
        }}
      >
        <ToolbarButton css={{ background: 'none' }} aria-label="Back" asChild>
          <Link to={previousStep?.href}>
            <KeyboardArrowLeftRounded />
          </Link>
        </ToolbarButton>

        <Text.Body variant="caption" weight="bold">
          New setup
        </Text.Body>

        {currentStep !== 'setupComplete' && (
          <StepsIndicatorRoot css={{ ml: 'auto' }}>
            <StepsIndicatorItem state={currentStep === 'index' ? 'active' : 'inactive'} />
            <StepsIndicatorItem state={currentStep === 'setupDisplayCapture' ? 'active' : 'inactive'} />
            <StepsIndicatorItem state={currentStep === 'setupAlerts' ? 'active' : 'inactive'} />
          </StepsIndicatorRoot>
        )}
      </Box>

      <ScrollAreaRoot
        css={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        type="always"
        asChild
      >
        <main>
          <ScrollAreaViewport css={{ px: '1rem', pt: '0.5rem', py: '1rem' }}>
            <FormProvider {...form}>
              <Outlet />
            </FormProvider>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar />
        </main>
      </ScrollAreaRoot>

      <Box as="footer" css={{ padding: '0.5rem', bgc: '$uiQuaternary' }}>
        {currentStep !== 'setupComplete' && (
          <Button
            color="neutral"
            density="compact"
            css={{ width: '100%' }}
            asChild={currentStep !== 'setupAlerts'}
            type="submit"
            disabled={handleSubmitMutation.isPending}
          >
            {handleSubmitMutation.isPending ? (
              <Text.Body>Setting everything up...</Text.Body>
            ) : currentStep === 'setupAlerts' ? (
              <Controller
                control={form.control}
                name="overlayTheme"
                render={() => {
                  return <>{routesCTA[currentStep](form.getValues())}</>;
                }}
              />
            ) : (
              <Link to={nextStep?.href}>{routesCTA[currentStep](form.getValues())}</Link>
            )}
          </Button>
        )}
        {currentStep === 'setupComplete' && (
          <Button
            color="neutral"
            density="compact"
            css={{ width: '100%' }}
            onClick={() => {
              window.host.getCurrentContainerProperties(({ id, type }) => {
                switch (type) {
                  case 'modalDialog':
                    window.host.endModalDialog(true, () => void 0);
                    break;
                  case 'dockingWidget':
                    window.host.removeDockingWidgetsByIds([id], () => void 0);
                    break;
                }
              });
            }}
          >
            Finish setup
          </Button>
        )}
      </Box>
    </Box>
  );
}
